import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { API_ENDPOINTS } from '../utils/config';
import { formatCurrency } from '../utils/currencyUtils';
import './RequestDetailModal.css';

interface RequestDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  requestId: string;
  userRole: string;
}

interface RequestDetail {
  _id: string;
  product_name: string;
  product_brand?: string;
  product_global_code?: string;
  product_company_code?: string; // Legacy field
  company_product_code?: string; // New dynamic field
  company_product_code_label?: string; // Dynamic label
  product_description?: string;
  
  // Product Specifications
  dimensions_text?: string;
  
  base_unit?: {
    unit: 'adet' | 'kg' | 'gr' | 'metre' | 'lt' | 'm2' | 'm3';
    conversion_factor: number;
  };
  
  quantity: number;
  unit: string;
  budget: number;
  currency: string;
  delivery_time?: string;
  importance: string;
  region: string[];
  category?: string;
  address?: string;
  current_supplier?: string;
  favorite_suppliers: string[];
  additional_specifications: string[];
  custom_specifications: Array<{key: string, value: string}>;
  status: string;
  phase: number;
  created_at: string;
  updated_at: string;
  user_info?: {
    name: string;
    email: string;
    department: string;
  };
  procurement_notes?: string;
  additional_notes?: string;
}

const RequestDetailModal: React.FC<RequestDetailModalProps> = ({ 
  isOpen, 
  onClose, 
  requestId, 
  userRole 
}) => {
  const { t } = useTranslation();
  const [requestDetail, setRequestDetail] = useState<RequestDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { token } = useAuth();

  useEffect(() => {
    if (isOpen && requestId) {
      fetchRequestDetail();
    }
  }, [isOpen, requestId]);

  const fetchRequestDetail = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(API_ENDPOINTS.REQUEST_DETAIL(requestId), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setRequestDetail(result.data);
      } else {
        setError(result.error || t('common.failed_load_request_details'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.an_error_occurred'));
    } finally {
      setLoading(false);
    }
  };


  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusText = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'phase_1': t('common.pending_admin_approval'),
      'phase_2': t('common.under_analysis'),
      'phase_3': t('common.searching_suppliers'),
      'phase_4': t('common.collecting_quotes'),
      'phase_5': t('common.under_evaluation'),
      'completed': t('common.completed'),
      'rejected': t('common.rejected')
    };
    return statusMap[status] || status;
  };

  const getImportanceText = (importance: string) => {
    const importanceMap: { [key: string]: string } = {
      'time': t('common.time_critical'),
      'price': t('common.price_critical'),
      'quality': t('common.quality_critical')
    };
    return importanceMap[importance] || importance;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content request-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('common.request_details')}</h2>
          <button className="close-button" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="modal-body">
          {loading && (
            <div className="loading">
              <div className="spinner"></div>
              <p>{t('common.loading_request_details')}</p>
            </div>
          )}

          {error && (
            <div className="error-message">
              <p>❌ {error}</p>
            </div>
          )}

          {requestDetail && (
            <div className="request-detail-content">
              {/* Header Information */}
              <div className="detail-section">
                <h3>{t('common.general_information')}</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>{t('common.request_id')}:</label>
                    <span>{requestDetail._id}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.status')}:</label>
                    <span className={`status-badge status-${requestDetail.status}`}>
                      {getStatusText(requestDetail.status)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.phase')}:</label>
                    <span>{t('common.phase')} {requestDetail.phase}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.created_at')}:</label>
                    <span>{formatDate(requestDetail.created_at)}</span>
                  </div>
                  {requestDetail.user_info && (
                    <div className="detail-item">
                      <label>{t('common.requested_by')}:</label>
                      <span>{requestDetail.user_info.name} ({requestDetail.user_info.department})</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Product Information */}
              <div className="detail-section">
                <h3>{t('common.product_information')}</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>{t('common.product_name')}:</label>
                    <span>{requestDetail.product_name}</span>
                  </div>
                  {requestDetail.product_brand && (
                    <div className="detail-item">
                      <label>{t('common.brand')}:</label>
                      <span>{requestDetail.product_brand}</span>
                    </div>
                  )}
                  {requestDetail.product_global_code && (
                    <div className="detail-item">
                      <label>{t('common.product_global_code')}:</label>
                      <span>{requestDetail.product_global_code}</span>
                    </div>
                  )}
                  {requestDetail.product_company_code && (
                    <div className="detail-item">
                      <label>{t('common.product_company_code')}:</label>
                      <span>{requestDetail.product_company_code}</span>
                    </div>
                  )}
                  {requestDetail.company_product_code && (
                    <div className="detail-item">
                      <label>{requestDetail.company_product_code_label || 'Company Product Code'}:</label>
                      <span>{requestDetail.company_product_code}</span>
                    </div>
                  )}
                  {requestDetail.dimensions_text && (
                    <div className="detail-item">
                      <label>{t('common.dimensions')}:</label>
                      <span>{requestDetail.dimensions_text}</span>
                    </div>
                  )}
                  {requestDetail.base_unit && (
                    <div className="detail-item">
                      <label>{t('common.base_unit')}:</label>
                      <span>{requestDetail.base_unit.unit} ({t('common.factor')}: {requestDetail.base_unit.conversion_factor})</span>
                    </div>
                  )}
                  <div className="detail-item">
                    <label>{t('common.quantity')}:</label>
                    <span>{requestDetail.quantity} {requestDetail.unit}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.budget')}:</label>
                    <span>{formatCurrency(requestDetail.budget, requestDetail.currency)}</span>
                  </div>
                </div>
              </div>

              {/* Request Information */}
              <div className="detail-section">
                <h3>{t('common.request_information')}</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>{t('common.priority')}:</label>
                    <span>{getImportanceText(requestDetail.importance)}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.category')}:</label>
                    <span>{requestDetail.category || t('common.not_specified')}</span>
                  </div>
                  <div className="detail-item">
                    <label>{t('common.region')}:</label>
                    <span>{Array.isArray(requestDetail.region) ? requestDetail.region.join(', ') : requestDetail.region}</span>
                  </div>
                  {requestDetail.delivery_time && (
                    <div className="detail-item">
                      <label>{t('common.delivery_time')}:</label>
                      <span>{requestDetail.delivery_time}</span>
                    </div>
                  )}
                  {requestDetail.address && (
                    <div className="detail-item">
                      <label>{t('common.shipped')}:</label>
                      <span>{requestDetail.address}</span>
                    </div>
                  )}
                  {requestDetail.current_supplier && (
                    <div className="detail-item">
                      <label>{t('common.current_supplier')}:</label>
                      <span>{requestDetail.current_supplier}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Specifications */}
              {requestDetail.additional_specifications && requestDetail.additional_specifications.length > 0 && (
                <div className="detail-section">
                  <h3>{t('common.additional_specifications')}</h3>
                  <div className="specifications-list">
                    {requestDetail.additional_specifications.map((spec, index) => (
                      <div key={index} className="spec-item">
                        <span>{spec}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom Specifications */}
              {requestDetail.custom_specifications && requestDetail.custom_specifications.length > 0 && (
                <div className="detail-section">
                  <h3>{t('common.custom_specifications')}</h3>
                  <div className="custom-specs-grid">
                    {requestDetail.custom_specifications.map((spec, index) => (
                      <div key={index} className="custom-spec-item">
                        <label>{spec.key}:</label>
                        <span>{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Favorite Suppliers */}
              {requestDetail.favorite_suppliers && requestDetail.favorite_suppliers.length > 0 && (
                <div className="detail-section">
                  <h3>{t('common.preferred_suppliers')}</h3>
                  <div className="suppliers-list">
                    {requestDetail.favorite_suppliers.map((supplier, index) => (
                      <div key={index} className="supplier-item">
                        <span>{supplier}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {(requestDetail.procurement_notes || requestDetail.additional_notes) && (
                <div className="detail-section">
                  <h3>{t('common.notes')}</h3>
                  {requestDetail.procurement_notes && (
                    <div className="note-item">
                      <label>{t('common.procurement_notes')}:</label>
                      <p>{requestDetail.procurement_notes}</p>
                    </div>
                  )}
                  {requestDetail.additional_notes && (
                    <div className="note-item">
                      <label>{t('common.additional_notes')}:</label>
                      <p>{requestDetail.additional_notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestDetailModal; 